// "Rotate your phone" overlay. Bolo3K is landscape-first, but iOS Safari
// offers no programmatic orientation lock (screen.orientation.lock() throws
// NotSupportedError there; Android Chrome only honors it in fullscreen), and
// the manifest's `orientation: landscape` only applies to the *installed*
// app on Android. So in-browser portrait play gets a polite full-screen
// prompt instead — shown only when ALL of:
//   1. the viewport is portrait  (matchMedia '(orientation: portrait)')
//   2. the device is phone-sized (small viewport — tablets play fine portrait)
//   3. a game is actually active (caller-supplied callback — never nag the lobby)
// and it is non-blocking: "Play in portrait anyway" hides it for the session.

import './rotateOverlay.css';

const SESSION_KEY = 'bolo3k.rotateOverlayDismissed';

/** Phone heuristic: the SHORT viewport side, in CSS px. Phones are ≤ ~450;
 *  small tablets start around 600. */
const PHONE_MAX_SHORT_SIDE = 500;

let overlay: HTMLElement | null = null;

function dismissedThisSession(): boolean {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1';
  } catch {
    return false; // storage blocked (private mode) — just don't remember
  }
}

function rememberDismissed(): void {
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
  } catch {
    // Storage blocked — the in-page hide below still works until reload.
  }
}

/**
 * Mount the rotate-hint overlay and keep its visibility in sync with
 * orientation / viewport / game state. Idempotent (second call is a no-op).
 *
 * @param isGameActive Live predicate — re-read on every evaluation.
 * @param root         Mount parent; defaults to document.body.
 */
export function initRotateOverlay(
  isGameActive: () => boolean,
  root: HTMLElement = document.body,
): void {
  if (overlay !== null) return;

  const el = document.createElement('div');
  el.className = 'pwa-rotate-overlay';
  el.hidden = true;

  const phone = document.createElement('div');
  phone.className = 'pwa-rotate-phone';
  phone.setAttribute('aria-hidden', 'true');

  const title = document.createElement('div');
  title.className = 'pwa-rotate-title';
  title.textContent = 'Rotate your phone';

  const sub = document.createElement('div');
  sub.className = 'pwa-rotate-sub';
  sub.textContent = 'Bolo2K is built for landscape — turn sideways for the full battlefield.';

  const dismiss = document.createElement('button');
  dismiss.className = 'pwa-rotate-dismiss';
  dismiss.type = 'button';
  dismiss.textContent = 'Play in portrait anyway';
  dismiss.addEventListener('click', () => {
    rememberDismissed();
    el.hidden = true;
  });

  el.append(phone, title, sub, dismiss);
  root.appendChild(el);
  overlay = el;

  const portraitMq = window.matchMedia('(orientation: portrait)');

  const evaluate = (): void => {
    const phoneSized =
      Math.min(window.innerWidth, window.innerHeight) <= PHONE_MAX_SHORT_SIDE;
    el.hidden = !(
      portraitMq.matches &&
      phoneSized &&
      isGameActive() &&
      !dismissedThisSession()
    );
  };

  portraitMq.addEventListener('change', evaluate);
  window.addEventListener('resize', evaluate);
  document.addEventListener('visibilitychange', evaluate);
  // isGameActive can flip with no accompanying UI event (e.g. the welcome
  // packet lands) — a coarse 1 s poll of one boolean covers that cheaply.
  window.setInterval(evaluate, 1000);
  evaluate();
}
